<script setup lang="ts">
/**
 * The open conversation's header band (MAR-948, out of `ChatPanel.vue`): new chat, the title with its
 * menu (favourite, rename, the superadmin JSON copy, delete), and — floating — expand and close.
 *
 * `ChatPanel` decides WHERE it renders (teleported into the app bar, or its own band); this decides
 * what is in it. `ownHeaderBand` only changes the band's own classes.
 */
import { ArrowLeft, Check, ChevronDown, Copy, Expand, PanelLeft, PanelLeftClose, Pencil, Plus, Share2, Star, Trash2, X } from 'lucide-vue-next'
import ChatShareDialog from '~/components/chat/ChatShareDialog.vue'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import type { ConversationDetail } from '~~/shared/chat/conversation'
import { messageCards } from '~/types/web-chat'

const props = defineProps<{
  compact: boolean
  /** The panel draws its own band (floating, or beside a document preview) instead of the app bar. */
  ownHeaderBand: boolean
  /** The rail's toggle lives in `AppBar`, which hides while a preview is open — it moves here. */
  showRailToggle: boolean
  cameFromHistory: boolean
}>()

const emit = defineEmits<{
  /** Leave this conversation for a fresh one. */
  'new-chat': []
}>()

const { t } = useI18n()
const router = useRouter()
const webChat = useWebChatStore()
const { apiFetch } = useApi()
const { profile } = useUserProfile()
const { toggleChatPanel, railExpanded, toggleRail } = usePanelLayout()
const isSuperAdmin = computed(() => !!profile.value?.is_superadmin)

// ── Share with a colleague (MAR-1417) ── needs an organization to share within.
const shareOpen = ref(false)
const canShare = computed(() => !!profile.value?.organization_name)

// ── Inline rename ──
const renaming = ref(false)
const renameInput = ref('')
const renameInputEl = ref<HTMLInputElement | null>(null)

function startRename() {
  renameInput.value = webChat.conversationTitle
  renaming.value = true
  nextTick(() => renameInputEl.value?.select())
}

async function submitRename() {
  const title = renameInput.value.trim()
  if (title && title !== webChat.conversationTitle) await webChat.renameConversation(title)
  renaming.value = false
}

async function handleDelete() {
  const id = webChat.conversationId
  if (!id) return
  // Leave it first, so nothing on screen is reading a conversation that no longer exists.
  emit('new-chat')
  await webChat.deleteConversation(id)
}

// ── Superadmin: copy the chat as JSON ──
const copiedChatJson = ref(false)

async function copyCurrentChatJson() {
  if (!webChat.conversationId) return
  // The PERSISTED conversation, 1:1 — the server is the source of truth. Not the live store, whose
  // tool calls are stream-reconstructed and can drift (the QRA's inner `read_legal_details` surfaced
  // via the progress sink once made it look like the chat agent called primitives directly).
  const conversation = await apiFetch<ConversationDetail>(`/api/conversations/${webChat.conversationId}`)

  // AND THE HALF THE DATABASE DOES NOT HOLD. The screen is messages PLUS cards, and a card is
  // in-memory by design — a run that failed showed its reason on screen and left no trace in the
  // paste, which cost an hour chasing the wrong cause. Kept separate and labelled: what was persisted
  // and what the user was looking at are answers to different questions.
  const clientState = {
    note: 'In-memory UI state, NOT persisted. Cards and run status live here, never in conversation_messages.',
    cards: webChat.messages
      .flatMap(m => messageCards(m).map(card => ({ messageId: m.id, toolId: card.toolId, request: card.request }))),
    activeRuns: useWorkflowRunsStore().activeRuns,
  }

  await navigator.clipboard.writeText(
    JSON.stringify({ exportedAt: new Date().toISOString(), conversation, clientState }, null, 2),
  )
  copiedChatJson.value = true
  window.setTimeout(() => { copiedChatJson.value = false }, 1500)
}

const quietButton = 'text-xs text-muted-foreground hover:text-foreground transition-colors px-1.5 py-1 rounded-md hover:bg-accent-lighter'
const iconButton = 'p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors'
</script>

<template>
  <!-- The drag handle only in the floating case: `useChatDock` looks for `data-chat-drag-handle`.
       Its buttons are draggable THROUGH — a press arms a drag, and the click is suppressed only if the
       pointer actually travelled (#362) — so a control added here never carves a dead zone out of the
       handle. The grab cursor is gated on `compact` alone: the preview's own band is not draggable. -->
  <div
    :data-chat-drag-handle="props.compact ? '' : undefined"
    :class="[
      props.ownHeaderBand
        ? 'h-(--header-h) px-3 shrink-0 flex items-center justify-between border-b'
        : 'flex flex-1 min-w-0 items-center justify-between gap-2',
      props.compact ? 'sm:cursor-grab sm:active:cursor-grabbing' : '',
    ]"
  >
    <div class="flex items-center gap-1 min-w-0">
      <button
        v-if="props.showRailToggle"
        type="button"
        class="flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors shrink-0"
        :title="railExpanded ? t('sidebar.collapse') : t('sidebar.expand')"
        :aria-label="railExpanded ? t('sidebar.collapse') : t('sidebar.expand')"
        :aria-expanded="railExpanded"
        @click="toggleRail"
      >
        <component :is="railExpanded ? PanelLeftClose : PanelLeft" class="h-3.5 w-3.5" />
      </button>
      <button v-if="props.cameFromHistory" type="button" :class="['flex items-center gap-1 shrink-0', quietButton]" @click="router.back()">
        <ArrowLeft class="h-3.5 w-3.5" />
        {{ t('nav.history') }}
      </button>
      <button type="button" :class="['flex items-center gap-1.5 shrink-0', quietButton]" :title="t('webChat.startNewChat')" @click="emit('new-chat')">
        <Plus class="h-3.5 w-3.5" />
        {{ t('webChat.newChat') }}
      </button>

      <template v-if="webChat.conversationId">
        <span class="text-muted-foreground mx-0.5 shrink-0" aria-hidden="true">/</span>

        <form v-if="renaming" class="flex items-center gap-1 min-w-0" @submit.prevent="submitRename">
          <input
            ref="renameInputEl"
            v-model="renameInput"
            class="text-xs h-6 px-1.5 rounded-md border border-input bg-background min-w-[120px] max-w-[200px] focus:outline-none focus:ring-1 focus:ring-ring"
            @keydown.escape="renaming = false"
            @blur="submitRename"
          >
        </form>

        <DropdownMenu v-else>
          <DropdownMenuTrigger as-child>
            <button type="button" :class="['flex items-center gap-1 min-w-0', quietButton]">
              <span class="truncate max-w-[180px]">{{ webChat.conversationTitle || t('common.untitled') }}</span>
              <ChevronDown class="h-3 w-3 shrink-0 opacity-60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent class="min-w-[180px]" align="start">
            <DropdownMenuItem class="px-2" @select="webChat.toggleFavorite()">
              <Star class="h-4 w-4" :class="webChat.conversationFavorite ? 'fill-warning-ink text-warning-ink' : ''" />
              {{ webChat.conversationFavorite ? t('history.removeFavorite') : t('history.addFavorite') }}
            </DropdownMenuItem>
            <DropdownMenuItem class="px-2" @select="startRename">
              <Pencil class="h-4 w-4" />
              {{ t('sidebar.rename') }}
            </DropdownMenuItem>
            <DropdownMenuItem v-if="canShare" class="px-2" data-testid="chat-share-open" @select="shareOpen = true">
              <Share2 class="h-4 w-4" />
              {{ t('chatShare.share') }}
            </DropdownMenuItem>
            <!-- A superadmin debug tool, folded into the menu rather than a standing icon every user sees. -->
            <DropdownMenuItem v-if="isSuperAdmin && webChat.messages.length > 0" class="px-2" @select="copyCurrentChatJson">
              <Check v-if="copiedChatJson" class="h-4 w-4 text-primary" />
              <Copy v-else class="h-4 w-4" />
              {{ t('webChat.copyChatJson') }}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <!-- A plain button, not a menu item: selecting an item closes the menu and would take the
                 confirmation with it (the case-delete overflow's pattern, MAR-474). -->
            <InlineConfirm :question="t('history.deleteConfirm')" :action="handleDelete">
              <template #default="{ arm }">
                <button
                  type="button"
                  class="flex w-full items-center gap-2 px-2 py-1.5 text-sm rounded-md cursor-pointer outline-none hover:bg-destructive/10 focus:bg-destructive/10 text-destructive transition-colors"
                  @click="arm"
                >
                  <Trash2 class="h-4 w-4" />
                  {{ t('common.delete') }}
                </button>
              </template>
            </InlineConfirm>
          </DropdownMenuContent>
        </DropdownMenu>
        <ChatShareDialog
          v-if="shareOpen && webChat.conversationId"
          :conversation-id="webChat.conversationId"
          @close="shareOpen = false"
        />
      </template>
    </div>
    <div class="flex items-center gap-1">
      <button
        v-if="props.compact && webChat.conversationId"
        type="button"
        :class="iconButton"
        :title="t('webChat.openFullView')"
        @click="router.push(`/app/c/${webChat.conversationId}`)"
      >
        <Expand class="h-3.5 w-3.5" />
      </button>
      <button v-if="props.compact" type="button" :class="['hidden sm:inline-flex', iconButton]" :title="t('common.close')" @click="toggleChatPanel">
        <X class="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
</template>
